import { FC } from 'react'
import { ImportProgressDTO } from '../../interfaces/Import/ShipmentBulkUploadApi'

interface ImportProgressBarProps {
  progress: ImportProgressDTO | null
  /** Compacto para usar dentro de una fila del dashboard. */
  compact?: boolean
}

/**
 * Barra de progreso de una carga masiva + contadores ✓/✗/⏳ (spec §3.3).
 * Calibrado a DESIGN.md: barra en `main` (env-aware); con errores tiñe el tramo
 * de error en rojo. Numérica con tabular-nums para que no "baile".
 */
export const ImportProgressBar: FC<ImportProgressBarProps> = ({
  progress,
  compact,
}) => {
  const total = progress?.total_details ?? 0
  const executed = progress?.executed_count ?? 0
  const errors = progress?.error_count ?? 0
  const pending = progress?.pending_count ?? Math.max(total - executed - errors, 0)
  const done = executed + errors
  const pct =
    progress?.progress_percentage != null
      ? Math.round(progress.progress_percentage)
      : total > 0
      ? Math.round((done / total) * 100)
      : 0

  // Anchos relativos del tramo OK (verde) y error (rojo) dentro de lo procesado.
  const okPct = total > 0 ? (executed / total) * 100 : 0
  const errPct = total > 0 ? (errors / total) * 100 : 0

  return (
    <div className="w-full">
      <div
        className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label="Progreso de la carga masiva"
      >
        <div
          className="absolute left-0 top-0 h-full bg-main-500 transition-all duration-300"
          style={{ width: `${okPct}%` }}
        />
        <div
          className="absolute top-0 h-full bg-red-500 transition-all duration-300"
          style={{ left: `${okPct}%`, width: `${errPct}%` }}
        />
      </div>

      <div
        className={
          'mt-1 flex items-center justify-between text-gray-600 tabular-nums ' +
          (compact ? 'text-xs' : 'text-sm')
        }
      >
        <span>
          {done} / {total} ({pct}%)
        </span>
        {!compact && (
          <span className="flex gap-3">
            <span className="text-green-700">✓ {executed}</span>
            <span className={errors > 0 ? 'text-red-600' : 'text-gray-400'}>
              ✗ {errors}
            </span>
            <span className="text-gray-500">⏳ {pending}</span>
          </span>
        )}
      </div>
    </div>
  )
}
